import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import {
  getAllBlocks,
  getUniversalMeasurementByProject
} from '../../services/api/implementation/impl';
import { PLCUnitSelectionModal } from './PLCUnitSelectionModal';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

export const CreatePLC = ({ handleListPLC, onClose, isSubmitting }) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [selectedUnitIdsByBlock, setSelectedUnitIdsByBlock] = useState({});
  const [selectedNumericRateType, setSelectedNumericRateType] = useState('cookie');

  // Measurement type for "Rate per sqft" label
  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sqft'
      : rawMeasurementUnit.replace('/', '');
  const cookieMeasurementType = measurementType.includes('/')
    ? measurementType
    : measurementType === 'sqmt'
    ? 'sq/mt'
    : 'sq/ft';

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      block_ids: [],
      mode: 'percent',
      price: ''
    }
  });

  const selectedBlocks = watch('block_ids') || [];
  const plcMode = watch('mode') || 'percent';

  // When a block is deselected, clear its unit selection
  useEffect(() => {
    const selectedIds = (selectedBlocks || []).map((b) => b.id);
    setSelectedUnitIdsByBlock((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((blockId) => {
        if (!selectedIds.includes(Number(blockId))) {
          delete next[blockId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedBlocks]);

  const { data: allBlocks, refetch: refetchBlocks } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const { data: universalMeasurements = [] } = useQuery({
    queryKey: ['universalMeasurementsByProject', projectId],
    queryFn: () => getUniversalMeasurementByProject(projectId),
    enabled: !!projectId
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Create P L C Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchBlocks();
  }, [projectId, refetchBlocks]);

  const blockOptions =
    allBlocks
      ?.filter((item) => item?.levels?.length > 0)
      ?.map((item) => ({ name: item.name, id: item.id })) || [];

  // Full block objects (with levels/units) for selected blocks — for unit selection modal
  const selectedBlocksWithLevels = useMemo(() => {
    if (!allBlocks?.length || !selectedBlocks?.length) return [];
    const ids = selectedBlocks.map((b) => b.id);
    return allBlocks
      .filter((b) => ids.includes(b.id))
      .map((b) => ({
        ...b,
        levels: [...(b.levels || [])]
          .filter(
            (lvl) =>
              lvl.floorType === 'RESIDENTIAL' || lvl.floorType === 'COMMERCIAL'
          )
          .sort((a, c) =>
            String(a?.name ?? '').localeCompare(String(c?.name ?? ''), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          )
      }))
      .filter((b) => b.levels?.length > 0);
  }, [allBlocks, selectedBlocks]);

  const onSubmit = (data) => {
    const sanitizedPrice = parseFloat(String(data.price || '0').replace(/[^\d.]/g, '')) || 0;
    const associated_unit_ids = Object.values(selectedUnitIdsByBlock || {}).flat();
    const isNumericMode = (data.mode || 'percent') === 'numeric';
    const isUmBased = isNumericMode && !!selectedUm;
    const perUnit = parseFloat(selectedUm?.per_unit) || 1;
    const convertedPrice = isUmBased
      ? parseFloat((sanitizedPrice / perUnit).toFixed(8))
      : sanitizedPrice;

    const payload = {
      name: data.name,
      description: data.description,
      mode: data.mode || 'percent',
      associated_unit_ids,
      block_ids: data.block_ids?.map((block) => block.id) || [],
      price: convertedPrice,
      ...(isUmBased ? { universal_measurement_id: selectedUm.id } : {})
    };
    handleListPLC(payload);
  };

  useEffect(() => {
    if (plcMode !== 'numeric') {
      setSelectedNumericRateType('cookie');
    }
  }, [plcMode]);

  const selectedUm = selectedNumericRateType.startsWith('um_')
    ? universalMeasurements.find((um) => `um_${um.id}` === selectedNumericRateType)
    : null;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-2 gap-3">
        {/* PLC Type Name */}
        <div>
          <label className="block mb-2">PLC Type Name *</label>
          <input
            {...register('name', {
              required: 'PLC Type Name is required',
              pattern: {
                value: /^\S.*$/,
                message: 'PLC Type Name cannot be empty or contain only spaces'
              }
            })}
            className="w-full p-2 border rounded"
          />
          {errors.name && <span className="text-red-500 text-sm">{errors.name.message}</span>}
        </div>

        {/* PLC Description */}
        <div>
          <label className="block mb-2">PLC Description *</label>
          <input
            {...register('description', { required: 'PLC Description is required' })}
            className="w-full p-2 border rounded"
          />
          {errors.description && (
            <span className="text-red-500 text-sm">{errors.description.message}</span>
          )}
        </div>

        {/* Associated Blocks */}
        <div className="col-span-2">
          <label className="block mb-2">Associated Blocks *</label>
          <Controller
            name="block_ids"
            control={control}
            defaultValue={[]}
            rules={{
              validate: (value) =>
                value && value.length > 0 ? true : 'At least one accommodation must be selected'
            }}
            render={({ field }) => (
              <>
                <div className="flex flex-wrap gap-4 p-3 border rounded bg-gray-50/50 min-h-[2.5rem]">
                  {blockOptions.map((block) => {
                    const isChecked =
                      Array.isArray(field.value) &&
                      field.value.some((b) => b.id === block.id);
                    return (
                      <label
                        key={block.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-100/80 rounded px-2 py-1"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const current = Array.isArray(field.value) ? field.value : [];
                            if (isChecked) {
                              field.onChange(current.filter((b) => b.id !== block.id));
                            } else {
                              field.onChange([...current, block]);
                            }
                          }}
                          className="rounded border-gray-300 accent-customBlue focus:ring-customBlue focus:ring-2"
                        />
                        <span className="text-sm">{block.name}</span>
                      </label>
                    );
                  })}
                </div>
                {errors.block_ids && (
                  <span className="text-red-500 text-sm">{errors.block_ids.message}</span>
                )}
              </>
            )}
          />
        </div>

        {/* Associated Units — shown when at least one block is selected */}
        {selectedBlocks.length > 0 && (
          <div className="col-span-2">
            <label className="block mb-2">Associated Units</label>
            <div className="flex flex-wrap gap-4 p-3 border rounded bg-gray-50/50 min-h-[2.5rem]">
              {selectedBlocks.map((block) => {
                const count = (selectedUnitIdsByBlock[block.id] || []).length;
                return (
                  <button
                    key={block.id}
                    type="button"
                    onClick={() => setIsUnitModalOpen(true)}
                    className="flex items-center gap-2 rounded px-3 py-1.5 hover:bg-gray-100/80 transition-colors text-left">
                    <span className="text-sm font-medium text-gray-800">
                      {block.name}
                    </span>
                    <span className="inline-flex items-center rounded-md bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                      {count} units
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Click a block name to select which units are associated with this PLC.
            </p>
          </div>
        )}

        {/* PLC Rate */}
        <div className="col-span-2 mt-4 pt-4 border-t border-stone-200">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rate_mode"
                  value="percent"
                  checked={plcMode === 'percent'}
                  onChange={() => {
                    setValue('mode', 'percent', {
                      shouldValidate: true,
                      shouldDirty: true
                    });
                    setSelectedNumericRateType('cookie');
                  }}
                  className="rounded-full border-gray-300 accent-customBlue focus:ring-customBlue focus:ring-2"
                />
                <span className="text-sm">Rate per %</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rate_mode"
                  value="cookie"
                  checked={plcMode === 'numeric' && selectedNumericRateType === 'cookie'}
                  onChange={() => {
                    setValue('mode', 'numeric', {
                      shouldValidate: true,
                      shouldDirty: true
                    });
                    setSelectedNumericRateType('cookie');
                  }}
                  className="rounded-full border-gray-300 accent-customBlue focus:ring-customBlue focus:ring-2"
                />
                <span className="text-sm">Rate per {cookieMeasurementType}</span>
              </label>
              {universalMeasurements.map((um) => (
                <label key={um.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rate_mode"
                    value={`um_${um.id}`}
                    checked={plcMode === 'numeric' && selectedNumericRateType === `um_${um.id}`}
                    onChange={() => {
                      setValue('mode', 'numeric', {
                        shouldValidate: true,
                        shouldDirty: true
                      });
                      setSelectedNumericRateType(`um_${um.id}`);
                    }}
                    className="rounded-full border-gray-300 accent-customBlue focus:ring-customBlue focus:ring-2"
                  />
                  <span className="text-sm">Rate per {um.name}</span>
                </label>
              ))}
            </div>
            <div className="flex-1 min-w-[200px] flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Enter Here</label>
              <div className="relative flex-1 max-w-[180px]">
                <input
                  {...register('price', {
                    required: 'Rate is required',
                    validate: (v) => {
                      const num = parseFloat(String(v || '').replace(/[^\d.]/g, ''));
                      if (isNaN(num) || num < 0) return 'Enter a valid number';
                      if (plcMode === 'percent' && num > 100) return 'Percentage cannot exceed 100';
                      return true;
                    }
                  })}
                  type="text"
                  placeholder={plcMode === 'percent' ? '0' : '0'}
                  className="w-full p-2 border rounded pr-8"
                  onChange={(e) => {
                    // Keep textbox in valid range for Percent mode.
                    const cleanedRaw = String(e.target.value || '').replace(/[^\d.]/g, '');
                    const parts = cleanedRaw.split('.');
                    let cleaned =
                      parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : cleanedRaw;

                    if (plcMode === 'percent' && cleaned !== '') {
                      const num = parseFloat(cleaned);
                      if (!isNaN(num) && num > 100) cleaned = '100';
                    }

                    e.target.value = cleaned;
                    setValue('price', cleaned, { shouldValidate: true, shouldDirty: true });
                  }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
                  {plcMode === 'percent'
                    ? '%'
                    : selectedUm?.name
                    ? `/${selectedUm.name}`
                    : `/${cookieMeasurementType}`}
                </span>
              </div>
            </div>
          </div>
          {errors.price && (
            <span className="text-red-500 text-sm block mt-1">{errors.price.message}</span>
          )}
        </div>
      </div>

      {isUnitModalOpen && (
        <Modal
          name="Select Units"
          width={60}
          onClose={() => setIsUnitModalOpen(false)}
          hideClose={false}>
          <div className="h-[28rem]">
            <PLCUnitSelectionModal
              blocks={selectedBlocksWithLevels}
              selectedUnitIdsByBlock={selectedUnitIdsByBlock}
              onConfirm={setSelectedUnitIdsByBlock}
              onClose={() => setIsUnitModalOpen(false)}
            />
          </div>
        </Modal>
      )}

      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="reset" displayType="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          Proceed
        </Button>
      </div>
    </form>
  );
};
